// import axios from "axios";
import Vue from 'vue'
import axios from '@/libs/axios'

const state = {
  isAuthenticated: false,
  isAuthenticatePending: true,
  isLogoutPending: false,
  error: null,
  user: null
};

const getters = {
  isAdmin(state) {
    if (!state.user) return false;
    return state.user.is_admin || state.user.role === 'admin';
  },
  // Permission 기반 getters (Vue 2: ?. 금지, && 사용)
  canManageUsers(state) {
    if (!state.user) return false;
    return (state.user.permissions && state.user.permissions.can_manage_users) || false;
  },
  canDeleteUsers(state) {
    if (!state.user) return false;
    return (state.user.permissions && state.user.permissions.can_delete_users) || false;
  },
  canManageSystem(state) {
    if (!state.user) return false;
    return (state.user.permissions && state.user.permissions.can_manage_system) || false;
  },
  canViewQuotas(state) {
    if (!state.user) return false;
    return (state.user.permissions && state.user.permissions.can_view_quotas) || false;
  },
  canManageKeys(state) {
    if (!state.user) return false;
    return (state.user.permissions && state.user.permissions.can_manage_keys) || false;
  },
  canViewDashboardAdmin(state) {
    if (!state.user) return false;
    return (state.user.permissions && state.user.permissions.can_view_dashboard_admin) || false;
  },
  // API 접근 가능 여부 (user_type이 api 또는 both)
  canAccessApi(state) {
    if (!state.user) return false;
    var userType = state.user.user_type || 'web';
    return userType === 'api' || userType === 'both';
  },
  // External API 전용 사용자 여부 (user_type이 api만)
  isApiOnlyUser(state) {
    if (!state.user) return false;
    var userType = state.user.user_type || 'web';
    return userType === 'api';
  },
  // 사용자의 role 반환
  userRole(state) {
    if (!state.user) return 'user';
    return state.user.role || 'user';
  },
  // 사용자의 user_type 반환
  userType(state) {
    if (!state.user) return 'web';
    return state.user.user_type || 'web';
  },
  // Flag 기반 getters
  isDistributor(state) {
    if (!state.user) return false;
    return (state.user.is_distributor) || false;
  },
  isBot(state) {
    if (!state.user) return false;
    return (state.user.is_bot) || false;
  },
  loginEnabled(state) {
    if (!state.user) return false;
    if (!state.user.anonymous) return true;
    return state.user.anonymous;
  },
  user(state) {
    return state.user;
  }
};

const mutations = {
  loggingIn(state) {
    state.isAuthenticatePending = true;
  },
  loggedIn(state, user) {
    state.user = user;
    state.isAuthenticatePending = false;
    state.isAuthenticated = true;
  },
  error(state, error) {
    state.error = error;
    state.isAuthenticatePending = false;
    state.isLogoutPending = false;
  },
  loggingOut(state) {
    state.isLogoutPending = true;
  },
  loggedOut(state) {
    state.user = null;
    state.isAuthenticated = false;
    state.isLogoutPending = false;
  },
  clearError(state) {
    state.error = null;
  },
  clearPasswordReminder(state) {
    // 비밀번호 변경/스누즈 성공 후 권장 팝업 flag 해제 (Vue2 반응성)
    if (state.user) Vue.set(state.user, 'password_change_recommended', false);
  },
  setUserInfo(state) {
    state.isAuthenticatePending = true;
    axios
      .get("/api/user/")
      .then(response => {
        state.user = response.data.user;
        state.isAuthenticated = true;
        state.isAuthenticatePending = false;
      })
      .catch(error => {
        // 로그인 전 401은 정상적인 응답이므로 에러를 콘솔에 표시하지 않음
        // (axios.js에서 이미 public endpoint로 처리되어 세션 만료 이벤트는 발생하지 않음)
        state.isAuthenticated = false;
        state.isAuthenticatePending = false;
        // 401이 아닌 다른 에러만 콘솔에 표시
        if (error.response && error.response.status !== 401) {
          console.error('Failed to get user info:', error);
        }
      });
  }
};

const actions = {
  async register({ commit, state }, { user, successCallback, errorCallback }) {
    if (state.isAuthenticated) return false;

    commit("clearError");
    commit("loggingIn");

    return axios
      .post("/api/user/register", {
        ...user
      })
      .then(response => {
        commit("loggedIn", response.data.user);
        if (successCallback != null) successCallback(response);
        return true;
      })
      .catch(error => {
        commit("error", error);
        if (errorCallback != null) errorCallback(error);
        return false;
      });
  },
  async login({ commit, state }, { user, successCallback, errorCallback }) {
    if (state.isAuthenticated) return false;

    commit("clearError");
    commit("loggingIn");

    return axios
      .post("/api/user/login", {
        ...user
      })
      .then(response => {
        commit("loggedIn", response.data.user);
        // 로그인 성공 이벤트 발생 (axios 인터셉터에서 사용)
        window.dispatchEvent(new CustomEvent('user-logged-in'))
        if (successCallback != null) successCallback(response);
        return true;
      })
      .catch(error => {
        commit("error", error);
        if (errorCallback != null) errorCallback(error);
        return false;
      });
  },
  async logout({ commit, state }) {
    // 이미 로그아웃된 상태면 상태만 정리하고 성공 반환
    if (!state.isAuthenticated) {
      commit("loggedOut");
      return true;
    }

    commit("loggingOut");
    commit("clearError");

    // Clear all session storage items
    sessionStorage.clear();

    return axios
      .get("/api/user/logout")
      .then(() => {
        commit("loggedOut");
        return true;
      })
      .catch(error => {
        // 401 에러는 이미 세션이 만료된 것이므로 정상 처리
        if (error.response && error.response.status === 401) {
          commit("loggedOut");
          return true;
        }
        commit("error", error);
        commit("loggedOut"); // 에러가 나도 상태는 정리
        return false;
      });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
